// =============================================================================
// Host-pluggable metrics channel, mirroring errorReporting.ts's design: this
// submodule is consumed by several apps and must not depend on a telemetry SDK,
// so counters/distributions are routed through a reporter the host registers at
// bootstrap (play-area wires Sentry in its index.tsx). A missing reporter is a
// silent no-op, so hosts that never register one are unaffected.
//
// Separate from errorReporting because these are *not* errors: they're the
// steady-state numbers (how long sessions live, how often they abort) that make
// a regression visible before anyone files a bug.
// =============================================================================

export type SharedMetricAttributes = Record<string, string | number | boolean>

/** count = monotonic counter, distribution = percentiles (p50/p95/max), gauge = latest value. */
export type SharedMetricKind = 'count' | 'distribution' | 'gauge'

export type SharedMetricReporter = (
    kind: SharedMetricKind,
    name: string,
    value: number,
    attributes?: SharedMetricAttributes,
    unit?: string,
) => void

/** Tags applied to every subsequent event the host reports, not just one metric. */
export type SharedTelemetryContextReporter = (tags: SharedMetricAttributes) => void

let metricReporter: SharedMetricReporter | undefined
let contextReporter: SharedTelemetryContextReporter | undefined

export const setSharedMetricReporter = (reporter: SharedMetricReporter): void => {
    metricReporter = reporter
}

export const setSharedTelemetryContextReporter = (reporter: SharedTelemetryContextReporter): void => {
    contextReporter = reporter
}

const emit = (
    kind: SharedMetricKind,
    name: string,
    value: number,
    attributes?: SharedMetricAttributes,
    unit?: string,
): void => {
    try {
        metricReporter?.(kind, name, value, attributes, unit)
    } catch {
        // Telemetry must never break the flow it observes.
    }
}

export const countMetric = (name: string, attributes?: SharedMetricAttributes, value = 1): void =>
    emit('count', name, value, attributes)

export const distributionMetric = (
    name: string,
    value: number,
    attributes?: SharedMetricAttributes,
    unit = 'millisecond',
): void => emit('distribution', name, value, attributes, unit)

export const gaugeMetric = (name: string, value: number, attributes?: SharedMetricAttributes, unit?: string): void =>
    emit('gauge', name, value, attributes, unit)

/**
 * Sets long-lived tags (session id/source) so every *unrelated* error the host
 * reports afterwards is filterable by how the session was established — the
 * cheapest way to spot "only restored sessions hit this bug".
 */
export const setTelemetryContext = (tags: SharedMetricAttributes): void => {
    try {
        contextReporter?.(tags)
    } catch {
        // Telemetry must never break the flow it observes.
    }
}
